import { Outlet, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../hooks/useAuthStore';
import { Box, Typography, Paper, Button } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

export const ClientGuard = () => {
  const { t } = useTranslation();
  const authStore = useAuthStore();
  const navigate = useNavigate();

  if (!authStore?.user?.osv.WOId) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #f5f7fa 0%, #e8ecf1 100%)',
          px: 2,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            maxWidth: 480,
            width: '100%',
            borderRadius: 4,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'grey.200',
            boxShadow: '0 8px 40px rgba(0,0,0,0.08)',
          }}
        >
          {/* Red accent bar at top */}
          <Box
            sx={{
              height: 6,
              background: 'linear-gradient(90deg, #d32f2f, #ef5350)',
            }}
          />

          <Box sx={{ p: { xs: 4, sm: 5 }, textAlign: 'center' }}>
            {/* Icon */}
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 72,
                height: 72,
                borderRadius: '50%',
                bgcolor: 'error.50',
                mb: 3,
                position: 'relative',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  borderRadius: '50%',
                  border: '2px solid',
                  borderColor: 'error.200',
                  opacity: 0.6,
                }}
              />
              <LockOutlinedIcon sx={{ fontSize: 32, color: 'error.main' }} />
            </Box>

            {/* Title */}
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: 'grey.900',
                mb: 1.5,
                letterSpacing: '-0.3px',
              }}
            >
              {t('error.access_denied', 'Dostop zavrnjen')}
            </Typography>

            {/* Divider line */}
            <Box
              sx={{
                width: 40,
                height: 3,
                bgcolor: 'error.main',
                borderRadius: 2,
                mx: 'auto',
                mb: 2.5,
              }}
            />

            {/* Main error message */}
            <Typography
              variant="body1"
              sx={{
                color: 'grey.700',
                lineHeight: 1.7,
                mb: 3,
              }}
            >
              {t(
                'error.woid_missing',
                'Nimate zadostnih pravic za dostop do podatkov. Obrnite se na SIQ za pomoč.'
              )}
            </Typography>

            {/* Info chip */}
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.75,
                bgcolor: 'grey.100',
                border: '1px solid',
                borderColor: 'grey.200',
                borderRadius: 2,
                px: 2,
                py: 1,
                mb: 4,
              }}
            >
              <ErrorOutlineIcon sx={{ fontSize: 16, color: 'grey.500' }} />
              <Typography variant="caption" sx={{ color: 'grey.600', fontWeight: 500 }}>
                {t('error.code', 'Koda napake')}: WOId_MISSING
              </Typography>
            </Box>

            {/* Action buttons */}
            <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center' }}>
              <Button
                variant="outlined"
                color="inherit"
                onClick={() => navigate(-1)}
                sx={{
                  borderColor: 'grey.300',
                  color: 'grey.700',
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 500,
                  '&:hover': { borderColor: 'grey.400', bgcolor: 'grey.50' },
                }}
              >
                {t('common.go_back', 'Nazaj')}
              </Button>
              <Button
                variant="contained"
                disableElevation
                onClick={() => navigate('/')}
                sx={{
                  bgcolor: 'grey.900',
                  color: 'white',
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 500,
                  '&:hover': { bgcolor: 'grey.800' },
                }}
              >
                {t('common.home', 'Domov')}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>
    );
  }

  return <Outlet />;
};
